'use client';

import { useState, useEffect } from 'react';
import BookRecommendation from './BookRecommendationServer';
import { BookRecommendationTranslations } from '@/lib/translations/types';
import { track } from '@vercel/analytics';

interface BookRecommendationMobileProps {
  className?: string;
  translations: BookRecommendationTranslations;
  bookData: {
    title: string;
    imageUrl: string;
    amazonUrl: string;
    affiliateSource: string;
    rating?: number;
  };
  articleSlug?: string;
}

export default function BookRecommendationMobile(props: BookRecommendationMobileProps) {
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleBookClick = () => {
    track('Book Recommendation Click', {
      book: props.bookData.title,
      url: props.articleSlug || ""
    });
  };

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Check if already dismissed in this session
    const dismissed = sessionStorage.getItem('bookRecommendationDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;

      // Show modal when user scrolls 25% and it's mobile
      if (scrollPercent >= 25 && window.innerWidth < 1024 && !isDismissed) {
        setShowMobileModal(true);
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    
    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isDismissed]);

  const handleDismiss = () => {
    setShowMobileModal(false);
    setIsDismissed(true);
    // Remember dismissal for this session
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('bookRecommendationDismissed', 'true');
    }
  };

  if (!props.bookData) return null;

  return (
    <>
      {/* Normal position (desktop and after mobile dismissal) */}
      <div className={`${isDismissed ? 'block' : 'hidden lg:block'}`}>
        <BookRecommendation {...props} onBookClick={handleBookClick} />
      </div>

      {/* Mobile modal */}
      {showMobileModal && !isDismissed && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleDismiss} />
          
          {/* Modal content */}
          <div className="absolute bottom-8 left-0 right-0 transform transition-transform duration-300 ease-out">
            <div className="rounded-t-lg mx-4 mb-4">
              {/* Close button */}
              <div className="flex justify-end p-2">
                <button
                  onClick={handleDismiss}
                  className="p-2 bg-white rounded-full text-black hover:text-gray-600 transition-colors"
                  aria-label="Close book recommendation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Book recommendation content */}
              <div className="px-4 pb-4">
                <BookRecommendation {...props} onBookClick={handleBookClick} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
